import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Save, Trash, X } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

const CATEGORIES = [
  { v: "konut", l: "Konut (Toplu İş)" },
  { v: "ev", l: "Ev" },
  { v: "isyeri", l: "İşyeri" },
];

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/projects"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ __new: true, title: "", description: "", category: "ev", furniture_type: "", location: "", date: "", images: [""], featured: false, order: items.length + 1, active: true });

  const save = async () => {
    const body = { ...editing }; delete body.__new; delete body.id; delete body._imgKeys;
    try {
      if (editing.__new) { await api.post("/projects", body); toast.success("Eklendi"); }
      else { await api.put(`/projects/${editing.id}`, body); toast.success("Güncellendi"); }
      setEditing(null); load();
    } catch (e) { toast.error("Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/projects/${id}`); toast.success("Silindi"); load();
  };

  const setImg = (i, v) => { const arr = [...editing.images]; arr[i] = v; setEditing({ ...editing, images: arr }); };
  const addImg = () => setEditing({ ...editing, images: [...(editing.images || []), ""], _imgKeys: [...(editing._imgKeys || (editing.images || []).map(() => crypto.randomUUID())), crypto.randomUUID()] });
  const rmImg = (i) => setEditing({
    ...editing,
    images: editing.images.filter((_, idx) => idx !== i),
    _imgKeys: (editing._imgKeys || []).filter((_, idx) => idx !== i),
  });

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-black text-ink-900">Çalışmalar</h1>
        <Button onClick={startNew} className="gradient-primary text-white rounded-full"><Plus className="h-4 w-4 mr-1" /> Yeni Ekle</Button>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          {items.map((it) => (
            <button key={it.id} onClick={() => setEditing({ ...it })} className={`text-left rounded-2xl overflow-hidden border-2 bg-white ${editing?.id === it.id ? "border-brand-500" : "border-ink-100"}`}>
              <div className="aspect-[4/3] overflow-hidden bg-ink-50">
                {it.images?.[0] && <img src={it.images[0]} alt={it.title} className="w-full h-full object-cover" />}
              </div>
              <div className="p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-600">{it.category}</div>
                <div className="font-semibold">{it.title}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="rounded-3xl bg-white border border-ink-100 p-6">
          {editing ? (
            <div className="space-y-4">
              <h3 className="font-display font-bold text-xl">{editing.__new ? "Yeni Proje" : "Düzenle"}</h3>
              <div><Label>Başlık</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Kategori</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Mobilya Türü</Label><Input value={editing.furniture_type || ""} onChange={(e) => setEditing({ ...editing, furniture_type: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Açıklama</Label><Textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Görseller</Label>
                <div className="space-y-2 mt-2">
                  {(editing.images || []).map((img, i) => {
                    const rowKey = editing._imgKeys?.[i] || `img-existing-${i}`;
                    return (
                      <div key={rowKey} className="flex gap-2 items-start">
                        <div className="flex-1">
                          <ImageUpload
                            value={img}
                            onChange={(url) => setImg(i, url)}
                            label=""
                            testId={`project-image-${i}`}
                          />
                        </div>
                        <Button variant="outline" onClick={() => rmImg(i)} className="rounded-full mt-2"><X className="h-4 w-4" /></Button>
                      </div>
                    );
                  })}
                  <Button variant="outline" onClick={addImg} className="rounded-full text-xs"><Plus className="h-3 w-3 mr-1" /> Görsel Ekle</Button>
                </div>
              </div>
              <div className="flex items-center gap-3"><Switch checked={!!editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} /><span className="text-sm">Öne Çıkarılmış</span></div>
              <div className="flex items-center gap-3"><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><span className="text-sm">Aktif</span></div>
              <div className="flex gap-2 pt-2">
                <Button onClick={save} className="gradient-primary text-white rounded-full"><Save className="h-4 w-4 mr-1.5" /> Kaydet</Button>
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">İptal</Button>
                {!editing.__new && <Button variant="outline" className="rounded-full text-red-600 border-red-300" onClick={() => del(editing.id)}><Trash className="h-4 w-4" /></Button>}
              </div>
            </div>
          ) : <div className="text-ink-500 text-sm">Bir çalışma seçin veya yeni ekleyin.</div>}
        </div>
      </div>
    </div>
  );
}
